import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { fieldValue, formatDate, postJson, RequestTimeoutError } from './accountForms';
import { shareApiBase } from './share/shareContract';

/**
 * Cancelling a scheduled organisation deletion. An administrator types their own username and
 * password (the same ones they sign in with); the server finds the organisations scheduled for
 * deletion that they administer and cancels it. If there is more than one, it asks which. Nothing
 * typed is reported to analytics, and a wrong login and "nothing for you to cancel" never say
 * anything about organisations beyond what the credentials could already see.
 */

/** `ORG_DELETION_CANCEL_PATH` in `@transport/contracts`, without the API base's `/api`. */
const CANCEL_PATH = '/public/org-deletion/cancel';
const TIMEOUT_MS = 30_000;

type Choice = { orgId: string; name: string; deleteAt: number };
type Status = { text: string; state: 'success' | 'error' | '' };
type ServerAnswer = {
  message?: string;
  code?: string;
  organisations?: Choice[];
  name?: string;
};

export function CancelDeletionForm() {
  const form = useRef<HTMLFormElement>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<Status>({ text: '', state: '' });
  const [choices, setChoices] = useState<Choice[]>([]);
  const [chosen, setChosen] = useState('');
  const [doneName, setDoneName] = useState<string>();

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const element = form.current;
    if (busy || !element || !element.reportValidity()) return;
    if (!shareApiBase()) {
      setStatus({
        text: 'Cancelling is not available on this site yet. Please write to us.',
        state: 'error',
      });
      return;
    }
    setBusy(true);
    setStatus({ text: 'Checking your details…', state: '' });
    try {
      const response = await postJson(
        `${shareApiBase()}${CANCEL_PATH}`,
        {
          username: fieldValue(element, 'username').trim(),
          password: fieldValue(element, 'password'),
          ...(chosen ? { orgId: chosen } : {}),
        },
        TIMEOUT_MS,
      );
      const body = (await response.json().catch(() => ({}))) as ServerAnswer;
      if (response.status === 409 && body.code === 'CHOOSE_ORGANISATION' && body.organisations) {
        setChoices(body.organisations);
        setChosen(body.organisations[0]?.orgId ?? '');
        setStatus({ text: 'Choose the organisation to keep.', state: '' });
        return;
      }
      if (!response.ok) {
        setStatus({
          text: body.message || 'We could not cancel the deletion. Please try again.',
          state: 'error',
        });
        return;
      }
      setDoneName(body.name || 'Your organisation');
      setStatus({ text: '', state: '' });
    } catch (error) {
      setStatus({
        text:
          error instanceof RequestTimeoutError
            ? 'The server took too long to answer. Check your connection and try again.'
            : 'We could not reach the server. Check your connection and try again.',
        state: 'error',
      });
    } finally {
      setBusy(false);
    }
  }

  if (doneName) {
    return (
      <div className="card trial-done" role="status">
        <div className="eyebrow">Deletion cancelled</div>
        <h2>{doneName} is kept.</h2>
        <p>
          Nothing was removed and everyone can sign in again. Every administrator has been told by
          email.
        </p>
        <div className="actions">
          <Link className="button secondary" to="/support">
            Support
          </Link>
        </div>
      </div>
    );
  }

  return (
    <form ref={form} className="card demo-form trial-form" onSubmit={submit} noValidate>
      <fieldset disabled={busy}>
        <legend>Administrator sign-in</legend>
        <div className="form-grid">
          <div className="field full">
            <label htmlFor="username">Username</label>
            <input
              id="username"
              name="username"
              required
              maxLength={120}
              autoComplete="username"
              autoCapitalize="none"
            />
          </div>
          <div className="field full">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              name="password"
              type="password"
              required
              maxLength={200}
              autoComplete="current-password"
            />
            <small>
              The ones you sign in to Transport Book with. They are checked, not stored.
            </small>
          </div>
          {choices.length > 1 && (
            <div className="field full">
              <label htmlFor="organisation">Organisation</label>
              <select
                id="organisation"
                name="organisation"
                value={chosen}
                onChange={event => setChosen(event.target.value)}
              >
                {choices.map(choice => (
                  <option key={choice.orgId} value={choice.orgId}>
                    {choice.name} (deleted on {formatDate(choice.deleteAt)})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </fieldset>
      <div className="actions">
        <button className="button primary" type="submit" disabled={busy}>
          {busy ? 'Working…' : 'Cancel the deletion'}
        </button>
      </div>
      <p
        className={`form-status ${status.state}`}
        role={status.state === 'error' ? 'alert' : 'status'}
        aria-live="polite"
      >
        {status.text}
      </p>
    </form>
  );
}
